import { useState, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCw, Check, X, Move } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export interface ImageCropAdjustModalProps {
  imageSrc: string;
  isOpen: boolean;
  onClose: () => void;
  onApplyCrop: (croppedBase64: string) => void;
  aspectRatio?: number; // ex: 1 para avatar (quadrado/redondo), 16/5 para capa de perfil (panorâmica)
  title?: string;
  isCircle?: boolean;
}

export function ImageCropAdjustModal({
  imageSrc,
  isOpen,
  onClose,
  onApplyCrop,
  aspectRatio = 1,
  title = 'Ajustar e Enquadrar Imagem',
  isCircle = false,
}: ImageCropAdjustModalProps) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (isOpen && imageSrc) {
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = imageSrc;
      img.onload = () => {
        imageRef.current = img;
      };
    }
  }, [isOpen, imageSrc]);

  if (!isOpen) return null;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleConfirm = () => {
    if (!imageRef.current) {
      onClose();
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Dimensões de saída de alta qualidade
    const outputWidth = aspectRatio === 1 ? 600 : 1200;
    const outputHeight = Math.round(outputWidth / aspectRatio);

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const img = imageRef.current;

    ctx.save();
    ctx.translate(outputWidth / 2, outputHeight / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Calcular proporção de preenchimento
    const imgRatio = img.width / img.height;
    let drawWidth = outputWidth;
    let drawHeight = outputHeight;

    if (imgRatio > aspectRatio) {
      drawWidth = outputHeight * imgRatio;
    } else {
      drawHeight = outputWidth / imgRatio;
    }

    ctx.drawImage(
      img,
      -drawWidth / 2 + position.x * (outputWidth / 400),
      -drawHeight / 2 + position.y * (outputHeight / 400),
      drawWidth,
      drawHeight,
    );
    ctx.restore();

    const croppedBase64 = canvas.toDataURL('image/jpeg', 0.92);
    onApplyCrop(croppedBase64);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-md animate-in fade-in">
      <div className="bg-card border border-border rounded-3xl shadow-2xl max-w-xl w-full p-6 space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Move className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base text-foreground tracking-tight">{title}</h3>
              <p className="text-xs text-muted-foreground">Arraste para enquadrar ou use o zoom para ajustar</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Viewport de Visualização Interativo */}
        <div
          className={`relative overflow-hidden bg-muted/60 border border-border flex items-center justify-center cursor-grab active:cursor-grabbing select-none ${
            isCircle ? 'h-72 rounded-full w-72 mx-auto ring-4 ring-primary/20' : 'h-64 sm:h-72 w-full rounded-2xl'
          }`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            src={imageSrc}
            alt="Preview Crop"
            draggable={false}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
              maxWidth: 'none',
              maxHeight: 'none',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            className="pointer-events-none transition-transform duration-75"
          />

          <div className="absolute bottom-2 left-2 px-2.5 py-1 rounded-md bg-black/60 text-white text-[11px] font-medium backdrop-blur-xs pointer-events-none">
            Arraste com o mouse para mover
          </div>
        </div>

        {/* Controles de Zoom e Rotação */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              type="range"
              min="0.8"
              max="3"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-primary h-2 bg-muted rounded-lg cursor-pointer"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-xs font-mono font-bold text-foreground w-12 text-right">
              {Math.round(zoom * 100)}%
            </span>
          </div>

          <div className="flex items-center justify-between text-xs pt-1">
            <button
              type="button"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border hover:bg-muted font-medium text-foreground transition-colors cursor-pointer"
            >
              <RotateCw className="h-3.5 w-3.5" />
              <span>Girar 90°</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setZoom(1);
                setRotation(0);
                setPosition({ x: 0, y: 0 });
              }}
              className="text-xs text-muted-foreground hover:text-foreground font-medium underline"
            >
              Redefinir Posição
            </button>
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
          <Button variant="outline" onClick={onClose} className="rounded-xl h-11 px-5">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} className="gap-2 rounded-xl h-11 px-6 font-bold shadow-md shadow-primary/20">
            <Check className="h-4 w-4" />
            <span>Aplicar Enquadramento</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
